import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Products — mirrors stacks-agents `src/app/api/products/`. */
@Injectable({ providedIn: 'root' })
export class ProductsApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  // ── read ──

  getProductsCount(projectId: string, searchText?: string): Promise<number> {
    // Built key by key: an omitted searchText would otherwise be sent as the
    // literal string "undefined", which the backend treats as a real search term.
    let params = new HttpParams().set('projectId', String(projectId))
    if (searchText !== undefined && searchText !== null && searchText !== '') params = params.set('searchText', String(searchText))

    return this.http
      .get<{ data?: number }>(`${environment.agentsUrl}v4/retrieval/products/count`, {
        headers: this.agentsAuthHeaders(),
        params,
      })
      .pipe(
        map(body => body?.data ?? 0),
        catchError(() => of(0)),
      )
      .toPromise() as Promise<number>
  }

  getProducts(projectId: string, page = 0, pageSize = 20, searchText?: string, sortBy?: string): Promise<any[]> {
    let params = new HttpParams()
      .set('projectId', String(projectId))
      .set('page', String(page))
      .set('pageSize', String(pageSize))
    if (searchText !== undefined && searchText !== null && searchText !== '') params = params.set('searchText', String(searchText))
    if (sortBy) params = params.set('sortBy', sortBy)

    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/products`, {
        headers: this.agentsAuthHeaders(),
        params,
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getAllProductsForExport(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/products/export`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getAllProducts(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/products/all`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getProductById(projectId: string, productId: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/products/by-id`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, productId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  getExistingProductIds(projectId: string, productIds: string[]): Promise<string[]> {
    return this.http
      .get<{ data?: string[] }>(`${environment.agentsUrl}v4/retrieval/products/existing-ids`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, productIds: productIds.join(',') },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<string[]>
  }

  getProductsCountByCategory(projectId: string, categoryId: string, searchText?: string): Promise<number> {
    let params = new HttpParams()
      .set('projectId', String(projectId))
      .set('categoryId', String(categoryId))
    if (searchText !== undefined && searchText !== null && searchText !== '') params = params.set('searchText', String(searchText))

    return this.http
      .get<{ data?: number }>(`${environment.agentsUrl}v4/retrieval/products/count-by-category`, {
        headers: this.agentsAuthHeaders(),
        params,
      })
      .pipe(
        map(body => body?.data ?? 0),
        catchError(() => of(0)),
      )
      .toPromise() as Promise<number>
  }

  getProductsByCategory(projectId: string, categoryId: string, page = 0, pageSize = 20, searchText?: string, sortBy?: string): Promise<any[]> {
    let params = new HttpParams()
      .set('projectId', String(projectId))
      .set('categoryId', String(categoryId))
      .set('page', String(page))
      .set('pageSize', String(pageSize))
    if (searchText !== undefined && searchText !== null && searchText !== '') params = params.set('searchText', String(searchText))
    if (sortBy) params = params.set('sortBy', sortBy)

    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/products/by-category`, {
        headers: this.agentsAuthHeaders(),
        params,
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  // ── write ──

  /** Creates a product with its attributes, variations, and category links. @returns The new product UUID, or undefined on failure. */
  async insertProduct(projectId: string, product: any): Promise<string | undefined> {
    const res = await this.http
      .post<{ data: { id: string } }>(`${environment.agentsUrl}v4/creation/products`,
        { projectId, product },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return res?.data?.id ?? undefined
  }

  /** Updates an existing product and re-syncs its child tables. @returns found=false when no matching row exists. */
  async updateProduct(projectId: string, productId: string, product: any): Promise<boolean> {
    const res = await this.http
      .patch<{ data: { found: boolean } }>(`${environment.agentsUrl}v4/update/products`,
        { projectId, productId, product },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    // Treat transport failures as "found" so callers don't create duplicates on flaky networks.
    return res?.data?.found ?? true
  }

  /** Toggles the publish flag on a product. */
  async setProductPublish(projectId: string, productId: string, publish: boolean): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/products/publish`,
        { projectId, productId, publish },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Replaces the image_urls array on a product. */
  async updateProductImages(projectId: string, productId: string, imageUrls: string[]): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/products/images`,
        { projectId, productId, imageUrls },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Soft-deletes a product. */
  async deleteProduct(projectId: string, productId: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/products`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, productId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
